import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Settings, DollarSign, TrendingUp, FileText, Shield,
  Package, AlertCircle, CheckCircle, Clock, XCircle,
  RefreshCw, ChevronRight, CreditCard,
  BarChart3, PieChart, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientAdminPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const [overview, setOverview] = useState(null);
  const [billing, setBilling] = useState(null);
  const [claims, setClaims] = useState(null);
  const [insuranceReports, setInsuranceReports] = useState(null);

  useEffect(() => {
    fetchOverview();
  }, [token]);

  useEffect(() => {
    if (activeTab === 'billing') fetchBilling();
    if (activeTab === 'claims') fetchClaims();
    if (activeTab === 'reports') fetchInsuranceReports();
  }, [activeTab, token]);

  const fetchOverview = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/client/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOverview(data);
      }
    } catch (err) {
      console.error('Failed to fetch overview:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBilling = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/client/billing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBilling(data);
      }
    } catch (err) {
      console.error('Failed to fetch billing:', err);
    }
  };

  const fetchClaims = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/client/insurance/claims`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClaims(data);
      }
    } catch (err) {
      console.error('Failed to fetch claims:', err);
    }
  };

  const fetchInsuranceReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/client/insurance/reports`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInsuranceReports(data);
      }
    } catch (err) {
      console.error('Failed to fetch insurance reports:', err);
    }
  };


  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'claims', label: 'Insurance Claims', icon: Shield },
    { id: 'reports', label: 'Reports', icon: PieChart }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'paid': return <DollarSign className="w-4 h-4 text-emerald-500" />;
      case 'denied': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-slate-500">Manage your account settings and view reports</p>
        </div>
        <Button variant="outline" onClick={fetchOverview} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            onClick={() => setActiveTab(tab.id)}
            className="whitespace-nowrap"
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
        
        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-6" data-testid="overview-section">
            <h2 className="text-lg font-semibold">Account Overview - {overview.current_month}</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <Package className="w-8 h-8 text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{overview.overview.total_shipments}</p>
                <p className="text-sm text-slate-500">Total Shipments</p>
                <p className="text-xs text-emerald-500 mt-1">+{overview.overview.month_shipments} this month</p>
              </div>
              
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <Shield className="w-8 h-8 text-purple-500 mb-2" />
                <p className="text-2xl font-bold">{overview.overview.total_screenings}</p>
                <p className="text-sm text-slate-500">Fraud Screenings</p>
                <p className="text-xs text-emerald-500 mt-1">+{overview.overview.month_screenings} this month</p>
              </div>
              
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <FileText className="w-8 h-8 text-amber-500 mb-2" />
                <p className="text-2xl font-bold">{overview.overview.total_insurance}</p>
                <p className="text-sm text-slate-500">Insurance Declarations</p>
              </div>
              
              {/* Era un estimado inventado (borradores viejos por el margen de una pestaña que no fijaba nada).
                  Lo cobrado de verdad esta en Revenue; el margen de cada comerciante, en Merchants (12-oct). */}
              <Link to="/dashboard/admin/revenue" className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 hover:ring-1 hover:ring-emerald-400" data-testid="revenue-link">
                <DollarSign className="w-8 h-8 text-emerald-500 mb-2" />
                <p className="text-sm font-semibold">Revenue</p>
                <p className="text-sm text-slate-500">What was actually charged. Margins are set per merchant in Merchants.</p>
              </Link>
            </div>

            {overview.subscription && (
              <div className="bg-gradient-to-r from-emerald-500 to-blue-500 rounded-xl p-6 text-white">
                <h3 className="font-semibold mb-2">Active Subscription</h3>
                <p className="text-2xl font-bold">{overview.subscription.plan_name}</p>
                <p className="text-sm opacity-80">${overview.subscription.price}/month</p>
              </div>
            )}
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && billing && (
          <div className="space-y-6" data-testid="billing-section">
            <h2 className="text-lg font-semibold">Billing & Usage</h2>

            {/* Current Usage */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6">
              <h3 className="font-medium mb-4">Current Month Usage</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-2xl font-bold">{billing.current_usage.shipments}</p>
                  <p className="text-sm text-slate-500">Shipments</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{billing.current_usage.fraud_screenings}</p>
                  <p className="text-sm text-slate-500">Fraud Screenings</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{billing.current_usage.insurance_declarations}</p>
                  <p className="text-sm text-slate-500">Insurance Declarations</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-500">Estimated Charges</p>
                <p className="text-2xl font-bold text-emerald-600">${billing.estimated_charges.total}</p>
              </div>
            </div>

            {/* Subscription */}
            {billing.subscription && (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
                <h3 className="font-medium mb-2">Active Subscription</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold">{billing.subscription.plan_name}</p>
                    <p className="text-slate-500">${billing.subscription.price}/month</p>
                  </div>
                  <Button variant="outline">Manage Subscription</Button>
                </div>
              </div>
            )}

            {/* Recent Transactions */}
            <div>
              <h3 className="font-medium mb-4">Recent Transactions</h3>
              {billing.transactions.length > 0 ? (
                <div className="space-y-2">
                  {billing.transactions.slice(0, 10).map((tx, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div>
                        <p className="font-medium">{tx.type}</p>
                        <p className="text-sm text-slate-500">{new Date(tx.date).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">${tx.amount}</p>
                        <p className={`text-xs ${tx.status === 'completed' ? 'text-emerald-500' : 'text-yellow-500'}`}>
                          {tx.status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No transactions yet</p>
              )}
            </div>
          </div>
        )}

        {/* Insurance Claims Tab */}
        {activeTab === 'claims' && claims && (
          <div className="space-y-6" data-testid="claims-section">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Insurance Claims</h2>
              <Button variant="outline" onClick={fetchClaims}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{claims.summary.total}</p>
                <p className="text-xs text-slate-500">Total Claims</p>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{claims.summary.pending}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{claims.summary.approved}</p>
                <p className="text-xs text-slate-500">Approved</p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{claims.summary.paid}</p>
                <p className="text-xs text-slate-500">Paid</p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">${claims.summary.total_paid}</p>
                <p className="text-xs text-slate-500">Total Paid</p>
              </div>
            </div>

            {/* Claims List */}
            {claims.claims.length > 0 ? (
              <div className="space-y-3">
                {claims.claims.map((claim, i) => (
                  <div key={i} className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(claim.status)}
                          <span className="font-medium capitalize">{claim.claim_type} Claim</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            claim.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                            claim.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                            claim.status === 'denied' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {claim.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{claim.description}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Filed: {new Date(claim.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">${claim.claim_amount}</p>
                        {claim.payout_amount && (
                          <p className="text-sm text-emerald-500">Paid: ${claim.payout_amount}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No insurance claims filed yet</p>
              </div>
            )}
          </div>
        )}

        {/* Insurance Reports Tab */}
        {activeTab === 'reports' && insuranceReports && (
          <div className="space-y-6" data-testid="reports-section">
            <h2 className="text-lg font-semibold">Insurance Reports</h2>

            {/* Summary */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                <h3 className="text-sm opacity-80">Total Premiums Paid</h3>
                <p className="text-3xl font-bold">${insuranceReports.premiums.total_premiums_paid}</p>
                <p className="text-sm opacity-80">{insuranceReports.premiums.total_declarations} declarations</p>
              </div>
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
                <h3 className="text-sm opacity-80">Total Claimed</h3>
                <p className="text-3xl font-bold">${insuranceReports.claims.total_claimed}</p>
                <p className="text-sm opacity-80">{insuranceReports.claims.total_claims} claims</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
                <h3 className="text-sm opacity-80">Total Paid Out</h3>
                <p className="text-3xl font-bold">${insuranceReports.claims.total_paid}</p>
                <p className="text-sm opacity-80">{insuranceReports.loss_ratio}% claim rate</p>
              </div>
            </div>

            {/* By Coverage Type */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6">
              <h3 className="font-medium mb-4">Premiums by Coverage Type</h3>
              {Object.keys(insuranceReports.premiums.by_coverage_type).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(insuranceReports.premiums.by_coverage_type).map(([type, data]) => (
                    <div key={type} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg">
                      <div>
                        <p className="font-medium capitalize">{type.replace('_', ' ')}</p>
                        <p className="text-sm text-slate-500">{data.count} declarations</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">${data.total_premium.toFixed(2)}</p>
                        <p className="text-xs text-slate-500">Coverage: ${data.total_coverage.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No data yet</p>
              )}
            </div>

            {/* By Claim Type */}
            {Object.keys(insuranceReports.claims.by_claim_type).length > 0 && (
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6">
                <h3 className="font-medium mb-4">Claims by Type</h3>
                <div className="space-y-3">
                  {Object.entries(insuranceReports.claims.by_claim_type).map(([type, data]) => (
                    <div key={type} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        <span className="font-medium capitalize">{type}</span>
                        <span className="text-xs bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded">
                          {data.count} claims
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">${data.total_claimed.toFixed(2)}</p>
                        <p className="text-xs text-emerald-500">Paid: ${data.total_paid.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientAdminPage;
